import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api";
import {
  getOnboardingStatus,
  type OnboardingStatus,
} from "@/lib/profile-api";

/**
 * apps/web :: components :: OnboardingGate
 *
 * Wraps authenticated post-login routes. On mount it pings
 * `GET /api/profile/onboarding-status`; if `onboardingCompleted` is false
 * (or the user has no profile row yet) we bounce to `/onboarding` while
 * preserving the attempted destination in router state so the wizard can
 * return there on submit.
 *
 * IMPORTANT: do NOT apply this gate to `/onboarding` itself — that creates
 * an infinite redirect loop. The router in `App.tsx` only wraps the
 * non-onboarding authenticated routes.
 *
 * Loading UX: a small status div mirroring `ProtectedRoute`'s style so the
 * chrome doesn't flash between states.
 */
interface GateState {
  kind: "loading" | "ready" | "redirect" | "error";
  status?: OnboardingStatus;
  errorMessage?: string;
}

export default function OnboardingGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [state, setState] = useState<GateState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function probe() {
      try {
        const status = await getOnboardingStatus();
        if (cancelled) return;
        if (!status.hasProfile || !status.onboardingCompleted) {
          setState({ kind: "redirect", status });
          return;
        }
        setState({ kind: "ready", status });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        setState({ kind: "error", errorMessage: message });
      }
    }
    void probe();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="onboarding-gate-loading"
        className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
      >
        {t("onboarding.loading")}
      </div>
    );
  }

  if (state.kind === "redirect") {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to="/onboarding" replace state={{ returnTo }} />;
  }

  if (state.kind === "error") {
    return (
      <div
        role="alert"
        data-testid="onboarding-gate-error"
        className="mx-auto mt-xl max-w-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
      >
        {t("onboarding.loadError", { message: state.errorMessage })}
      </div>
    );
  }

  return <>{children}</>;
}
